import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';

interface Props {
  domainId: string;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const STATUS_STYLES: Record<string, { dot: string; label: string }> = {
  dispatched: { dot: 'bg-[#1D9E75]',  label: 'Dispatched' },
  failed:     { dot: 'bg-red-400',    label: 'Failed' },
  retrying:   { dot: 'bg-amber-400',  label: 'Retrying' },
  abandoned:  { dot: 'bg-red-300',    label: 'Abandoned' },
};

const BUCKET_LABELS: Record<string, string> = {
  schema:      'Schema',
  meta_header: 'Meta / headers',
  citation:    'Citations',
  content:     'Content',
};

export function AfiActivityPage({ domainId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['afi-recent-deployments', domainId, 50],
    queryFn: () => api.afi.recentDeployments(domainId, 50),
    staleTime: 30_000,
  });

  const events = data?.events ?? [];

  return (
    <div className="min-h-screen bg-[#F1F5F9] p-8">
      <div className="mb-8">
        <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#1D9E75] mb-1">
          Auto-fix
        </p>
        <h1 className="text-2xl font-semibold text-[#0F172A]">Activity</h1>
        <p className="text-sm text-[#475569] mt-1">
          All recent fix dispatch events for this domain.
        </p>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-[6px] divide-y divide-[#E2E8F0] animate-pulse">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between px-6 py-4 min-h-[56px]">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-gray-200" />
                <div className="h-4 w-56 bg-gray-100 rounded" />
              </div>
              <div className="h-3 w-28 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-[6px] p-10 text-center">
          <p className="text-sm font-medium text-[#0F172A]">No activity yet</p>
          <p className="text-xs text-[#94A3B8] mt-1">
            Deployment events will appear here once AFI starts dispatching fixes.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-[6px] divide-y divide-[#E2E8F0]">
          {events.map((event) => {
            const style = STATUS_STYLES[event.dispatchStatus] ?? STATUS_STYLES['dispatched']!;
            const bucketLabel = BUCKET_LABELS[event.fixBucket] ?? event.fixBucket;
            return (
              <div
                key={event.id}
                className="flex items-center justify-between px-6 py-4 min-h-[56px]"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
                  <div className="min-w-0">
                    <p className="text-sm text-[#0F172A]">
                      {bucketLabel} — {style.label}
                      {event.dispatchAttempt > 1 && (
                        <span className="text-[#94A3B8] ml-1.5 text-xs">
                          (attempt {event.dispatchAttempt})
                        </span>
                      )}
                    </p>
                    {event.failureReason && (
                      <p className="text-xs text-red-500 mt-0.5 truncate">{event.failureReason}</p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-[#94A3B8] shrink-0 ml-4 whitespace-nowrap">
                  {formatDate(event.dispatchedAt)}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
